import { CancelToken } from './AxiosManager';
import { AxiosError, AxiosInstance, CancelTokenSource } from 'axios';

interface aborted {
    proxy: {
        controller: AbortController;
    };
    revoke: () => void;
}

export class APIManager {
    private static controller: AbortController | null = null;

    static target = new EventTarget();

    private boundTarget = new MessageChannel();

    private abortProtocol: aborted | null = null;

    getCancelSource(): CancelTokenSource {
        return CancelToken.source();
    }

    getBoundEvent(): MessageChannel {
        return this.boundTarget;
    }

    setMIAChannel(): void {
        this.boundTarget = new MessageChannel();
    }

    getConfig(auth: boolean, headers = {}, config = {}): any {
        APIManager.controller = new AbortController();
        this.abortProtocol = this.getAbortProtocolProxy(APIManager.controller);

        const configHeader: any = {
            headers: {
                'append-auth-header': auth ? 'true' : 'false',
                ...headers,
            },
            signal: APIManager.controller.signal,
            ...config,
        };
        return configHeader;
    }

    getAbortProtocolProxy(controller: AbortController): aborted {
        return Proxy.revocable(
            {
                controller,
            },
            {
                get(target, prop) {
                    // eslint-disable-next-line prefer-rest-params
                    return Reflect.get(target, prop, this);
                },
            }
        );
    }

    getAbortProxy(): aborted | null {
        return this.abortProtocol;
    }

    getAbortInterceptor(provider: AxiosInstance): number {
        const abortProxy = this.getAbortProxy();
        const portOne = this.boundTarget.port1;
        // eslint-disable-next-line no-proto
        const portTwo = this.boundTarget.port2;
        // eslint-disable-next-line no-proto
        return provider.interceptors.response.use(
            (response) => {
                if (abortProxy !== null) {
                    try {
                        if (response.status === 200) {
                            APIManager.target.dispatchEvent(
                                new CustomEvent('revocation')
                            );
                            portOne.postMessage('revocation');
                            portTwo.postMessage('revocation');
                        } else {
                            APIManager.target.dispatchEvent(
                                new CustomEvent('revoke')
                            );
                            portOne.postMessage('revoke');
                            portTwo.postMessage('revoke');
                        }

                        abortProxy.revoke();
                    } catch (error) {
                        // console.log(error);
                    }
                }
                return response;
            },
            (reject) => {
                if (abortProxy !== null) {
                    try {
                        APIManager.target.dispatchEvent(
                            new CustomEvent('revoke')
                        );
                        portOne.postMessage('revoke');
                        portTwo.postMessage('revoke');
                        abortProxy.revoke();
                    } catch (error) {
                        // console.log(error);
                    }
                }
                return reject;
            }
        );
    }

    sendPost(
        url: any | string,
        postData: any,
        auth = false,
        headers = {},
        config = {},
        provider: AxiosInstance
    ): Promise<any> {
        const configurated = this.getConfig(auth, headers, config);
        const interceptors = this.getAbortInterceptor(provider);

        return new Promise(async (resolve, reject) => {
            provider
                .post(url, postData, configurated)
                .then((res: any) => {
                    if (res instanceof AxiosError) {
                        reject(res);
                    }
                    resolve(res);
                })
                .catch((e: any) => reject(e))
                .finally(() => {
                    provider.interceptors.response.eject(interceptors);
                });
        });
    }

    sendPatch(
        url: any | string,
        postData: any,
        auth = false,
        headers = {},
        config = {},
        provider: any
    ): Promise<any> {
        const configurated = this.getConfig(auth, headers, config);
        const interceptors = this.getAbortInterceptor(provider);

        return new Promise(async (resolve, reject) => {
            provider
                .patch(url, postData, configurated)
                .then((res: any) => {
                    if (res instanceof AxiosError) {
                        reject(res);
                    }
                    resolve(res);
                })
                .catch((e: any) => reject(e))
                .finally(() => {
                    provider.interceptors.response.eject(interceptors);
                });
        });
    }

    sendGet(
        url: any | string,
        auth = false,
        headers = {},
        config = {},
        provider: any
    ): Promise<any> {
        const configurated = this.getConfig(auth, headers, config);
        const interceptors = this.getAbortInterceptor(provider);
        return new Promise(async (resolve, reject) => {
            provider
                .get(url, configurated)
                .then((res: any) => {
                    if (res instanceof AxiosError) {
                        reject(res);
                    }
                    resolve(res);
                })
                .catch((e: any) => reject(e))
                .finally(() => {
                    provider.interceptors.response.eject(interceptors);
                });
        });
    }

    sendGetWithBody(
        url: any | string,
        postData: any,
        auth = false,
        provider: any
    ): Promise<any> {
        const configurated = this.getConfig(auth);
        configurated.params = postData;
        const interceptors = this.getAbortInterceptor(provider);
        return new Promise(async (resolve, reject) => {
            provider
                .get(url, configurated)
                .then((res: any) => {
                    if (res instanceof AxiosError) {
                        reject(res);
                    }
                    resolve(res);
                })
                .catch((e: any) => reject(e))
                .finally(() => {
                    provider.interceptors.response.eject(interceptors);
                });
        });
    }

    sendDelete(
        url: any | string,
        auth = false,
        headers = {},
        config = {},
        provider: any
    ): Promise<any> {
        const configurated = this.getConfig(auth, headers, config);
        const interceptors = this.getAbortInterceptor(provider);

        return new Promise(async (resolve, reject) => {
            provider
                .delete(url, configurated)
                .then((res: any) => {
                    if (res instanceof AxiosError) {
                        reject(res);
                    }
                    resolve(res);
                })
                .catch((e: any) => reject(e))
                .finally(() => {
                    provider.interceptors.response.eject(interceptors);
                });
        });
    }
}

export default new APIManager();
